/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { Fragment, lazy, memo, Suspense, useMemo, useState } from 'react';

import { styled } from '@compiled/react';
import { useIntl } from 'react-intl-next';

import Button, { LinkButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import Link from '@atlaskit/link';
import { SmartCardProvider as Provider } from '@atlaskit/link-provider';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { ProfileCardTrigger } from '@atlaskit/profilecard';
import { token } from '@atlaskit/tokens';
import Tooltip, { TooltipPrimitive } from '@atlaskit/tooltip';

import { descriptionHelper } from '../ai-answer-dialog/ai-answer-body/ai-answer-result/json/recent-work-list/description-helper';
import { useAIAnswerContext } from '../ai-answer-dialog/ai-answer-context';
import {
	type NLPSearchWhoResultCollaboratorDataType,
	type NLPSearchWorkResultRecentActivityDataType,
} from '../ai-answer-dialog/types';
import { SainAction, useAnalytics, useSAINUsageCriteriaMet } from '../common/controller/analytics';
import { ErrorBoundary } from '../common/ui/error-boundary';
import { messages } from '../messages';

import { onKnowledgeCardLinkClick } from './analytics';
import { KnowledgeCardsRow } from './knowledge-cards-row';
import { PersonCard } from './person-knowledge-card/person-card';
import { KnowledgeCardsIntentType, KnowledgeCardsRowType } from './types';
import { useTeamsAppLinks } from './use-teams-app-links';
import { createProfileClient, trimARI } from './util';

const styles = cssMap({
	bodyContainer: {
		position: 'relative',
	},
	fullInline: {
		width: '100%',
	},
	knowledgeCardsContent: {
		paddingBottom: token('space.050'),
	},
});

type KnowledgeCardsBodyProps = {
	currentUserId: string;
	recommendedUsers: NLPSearchWhoResultCollaboratorDataType[];
	recentActivity: NLPSearchWorkResultRecentActivityDataType[];
	profileUrl?: string;
	viewAllUrl?: string;
} & (
	| { type: KnowledgeCardsIntentType.PERSON }
	| { type: KnowledgeCardsIntentType.TEAM; teamId: string }
);

const EmptyState = ({ text }: { text: string }) => {
	return (
		<Box paddingInline="space.200" paddingBlock="space.075">
			<Text color="color.text.subtle">{text}</Text>
		</Box>
	);
};

const HoverCard = lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_.smart-card/hover-card" */ '@atlaskit/smart-card/hover-card'
	).then(({ HoverCard }) => ({
		default: HoverCard,
	})),
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-unsafe-style-overrides
const CardContainer = styled(TooltipPrimitive)({
	width: '214px',
	height: '236px',
});
const EXPANDED_MAX_ITEMS_SHOWN = 6;
const MAX_ITEMS_SHOWN = 3;

export const KnowledgeCardsBody = memo((props: KnowledgeCardsBodyProps) => {
	const { type, currentUserId, recommendedUsers, recentActivity, profileUrl, viewAllUrl } = props;
	const {
		commonAttributes,
		userDetails: { id: searcherId },
	} = useAIAnswerContext();
	const [fireAnalyticsEvent] = useAnalytics();
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
	const { formatMessage, formatDate } = useIntl();
	const [isExpanded, setExpanded] = useState(false);
	const isPerson = type === KnowledgeCardsIntentType.PERSON;
	const { viewAllLink, viewAllOnNavigate, worksWithHref, worksWithOnNavigate, toUser } =
		useTeamsAppLinks({
			type,
			currentUserId,
			viewAllUrl,
			cloudId: commonAttributes.cloudId,
			teamId: type === KnowledgeCardsIntentType.TEAM ? props.teamId : undefined,
		});

	const profileClient = useMemo(
		() => createProfileClient(commonAttributes.cloudId),
		[commonAttributes.cloudId],
	);

	return (
		<Box xcss={styles.bodyContainer}>
			<Stack>
				<Inline space="space.200">
					<Stack grow="fill" space="space.050" xcss={styles.knowledgeCardsContent}>
						<Box
							paddingInlineStart="space.200"
							paddingBlockStart="space.150"
							paddingInlineEnd="space.300"
							paddingBlockEnd="space.075"
						>
							<Inline spread="space-between" alignBlock="center">
								<Heading size="xsmall">
									{formatMessage(messages.knowledge_cards_recent_work_subheader)}
								</Heading>
								{recentActivity.length > 0 && (
									<Link
										href={viewAllLink}
										appearance="subtle"
										onClick={(e) => {
											fireAnalyticsEvent(
												onKnowledgeCardLinkClick({
													...commonAttributes,
													location: 'recent-work-view-all',
												}),
											);
											fireSAINUsageEvent({ metCriteria: SainAction.RECENT_WORK_CLICKED });
											if (fg('platform-adopt-teams-nav-config')) {
												viewAllOnNavigate(e);
											}
										}}
									>
										<Text color="color.text.subtle">
											{formatMessage(messages.knowledge_cards_view_all_link)}
										</Text>
									</Link>
								)}
							</Inline>
						</Box>
						<ErrorBoundary fallback={<Fragment />}>
							<Suspense fallback={<Fragment />}>
								<Provider>
									{!recentActivity.length ? (
										<EmptyState text={formatMessage(messages.knowledge_cards_recent_work_empty)} />
									) : (
										recentActivity
											.slice(
												0,
												!isExpanded
													? MAX_ITEMS_SHOWN
													: Math.min(recentActivity.length, EXPANDED_MAX_ITEMS_SHOWN),
											)
											.map((activity) => (
												<HoverCard
													url={activity.url}
													key={`knowledge-card-activity-${activity.url}`}
												>
													<KnowledgeCardsRow
														primaryText={activity.title}
														secondaryText={`${activity.containerName} • ${descriptionHelper({
															currentUserId: searcherId || '',
															event: activity.event,
															contributors: activity.contributors,
															othersCount: activity.othersCount,
															formatMessage,
															formatDate,
															timestamp: new Date(activity.timestamp),
														})}`}
														pictureUrl={activity.iconUrl}
														type={KnowledgeCardsRowType.WORK}
														onClick={() => {
															fireAnalyticsEvent(
																onKnowledgeCardLinkClick({
																	...commonAttributes,
																	location: 'recent-work-item',
																}),
															);
															fireSAINUsageEvent({ metCriteria: SainAction.RECENT_WORK_CLICKED });
															window.open(activity.url, '_self', 'noopener, noreferrer');
														}}
													/>
												</HoverCard>
											))
									)}
								</Provider>
							</Suspense>
						</ErrorBoundary>
					</Stack>
					<Stack grow="fill" space="space.050" xcss={styles.knowledgeCardsContent}>
						<Box
							paddingInlineStart="space.200"
							paddingBlockStart="space.150"
							paddingInlineEnd="space.150"
							paddingBlockEnd="space.075"
						>
							<Inline spread="space-between" alignBlock="center" xcss={styles.fullInline}>
								<Heading size="xsmall">
									{isPerson
										? formatMessage(messages.knowledge_cards_collaborators_subheader)
										: formatMessage(messages.knowledge_cards_team_members_subheader)}
								</Heading>
								{recommendedUsers.length > 0 && (
									<Link
										href={
											isPerson
												? fg('platform-adopt-teams-nav-config')
													? worksWithHref
													: `https://hello.atlassian.net/people/${currentUserId || ''}#workswith`
												: viewAllLink
										}
										appearance="subtle"
										onClick={(e) => {
											if (isPerson) {
												fireAnalyticsEvent(
													onKnowledgeCardLinkClick({
														...commonAttributes,
														location: 'reporting-lines-link',
													}),
												);
												fireSAINUsageEvent({ metCriteria: SainAction.REPORTING_LINES_CLICKED });
												if (fg('platform-adopt-teams-nav-config')) {
													worksWithOnNavigate(e);
												}
											} else {
												fireAnalyticsEvent(
													onKnowledgeCardLinkClick({
														...commonAttributes,
														location: 'recent-work-view-all',
													}),
												);
												fireSAINUsageEvent({ metCriteria: SainAction.RECENT_WORK_CLICKED });
												if (fg('platform-adopt-teams-nav-config')) {
													viewAllOnNavigate(e);
												}
											}
										}}
									>
										<Text color="color.text.subtle">
											{formatMessage(messages.knowledge_cards_view_all_link)}
										</Text>
									</Link>
								)}
							</Inline>
						</Box>
						{!recommendedUsers.length ? (
							<EmptyState
								text={formatMessage(
									isPerson
										? messages.knowledge_cards_collaborators_empty
										: messages.knowledge_cards_teammembers_empty,
								)}
							/>
						) : (
							recommendedUsers
								.slice(
									0,
									!isExpanded
										? MAX_ITEMS_SHOWN
										: Math.min(recommendedUsers.length, EXPANDED_MAX_ITEMS_SHOWN),
								)
								.map((user) => {
									const userUrl = `/people/${trimARI(user.accountId || '')}`;

									if (fg('kd_platform_person_cards')) {
										return (
											<ProfileCardTrigger
												userId={trimARI(user.accountId || '')}
												resourceClient={profileClient}
												actions={[
													{
														label: formatMessage(
															messages.knowledge_cards_person_hover_profile_link,
														),
														link: toUser(user.accountId || '').href,
														callback: (e) => {
															toUser(user.accountId || '').onNavigate(e);
														},
													},
												]}
											>
												<KnowledgeCardsRow
													primaryText={user.name}
													secondaryText={
														!!user.title || !!user.department
															? [user.title, user.department].filter((str) => !!str).join(', ')
															: ''
													}
													pictureUrl={user.picture || ''}
													type={KnowledgeCardsRowType.PERSON}
													onClick={(e: React.MouseEvent<HTMLDivElement>) => {
														fireAnalyticsEvent(
															onKnowledgeCardLinkClick({
																...commonAttributes,
																location: 'user-list-item',
															}),
														);
														fireSAINUsageEvent({ metCriteria: SainAction.TEAM_MEMBER_CLICKED });
														if (fg('platform-adopt-teams-nav-config')) {
															toUser(user.accountId || '').onNavigate(e);
														} else {
															window.open(user.url || userUrl, '_self', 'noopener, noreferrer');
														}
													}}
												/>
											</ProfileCardTrigger>
										);
									}
									return (
										<Tooltip
											key={user.accountId}
											position="bottom-end"
											// @ts-ignore -- `styled(TooltipPrimitive)` is not valid for `component` (and fails in Jira)
											component={CardContainer}
											content={
												<PersonCard
													key={user.accountId}
													name={user.name}
													profilePicUrl={user.picture || ''}
													accountId={user.accountId || ''}
													jobTitle={user.title}
													profileUrl={user.url || userUrl}
													email={user.email}
												/>
											}
										>
											{(triggerProps) => (
												<KnowledgeCardsRow
													primaryText={user.name}
													secondaryText={
														!!user.title || !!user.department
															? [user.title, user.department].filter((str) => !!str).join(', ')
															: ''
													}
													pictureUrl={user.picture || ''}
													type={KnowledgeCardsRowType.PERSON}
													triggerProps={triggerProps}
													onClick={(e: React.MouseEvent<HTMLDivElement>) => {
														fireAnalyticsEvent(
															onKnowledgeCardLinkClick({
																...commonAttributes,
																location: 'user-list-item',
															}),
														);
														fireSAINUsageEvent({ metCriteria: SainAction.TEAM_MEMBER_CLICKED });
														if (fg('platform-adopt-teams-nav-config')) {
															toUser(user.accountId || '').onNavigate(e);
														} else {
															window.open(user.url || userUrl, '_self', 'noopener, noreferrer');
														}
													}}
												/>
											)}
										</Tooltip>
									);
								})
						)}
					</Stack>
				</Inline>
				<Box paddingInline="space.200" paddingBlockStart="space.050" paddingBlockEnd="space.075">
					{recommendedUsers.length > MAX_ITEMS_SHOWN || recentActivity.length > MAX_ITEMS_SHOWN ? (
						<Inline alignInline="center" space="space.025">
							{isExpanded ? (
								<LinkButton
									href={profileUrl || ''}
									onClick={() => {
										fireAnalyticsEvent(
											onKnowledgeCardLinkClick({
												...commonAttributes,
												location: 'footer-view-all',
											}),
										);
										fireSAINUsageEvent({
											metCriteria: isPerson
												? SainAction.VIEW_PROFILE_CLICKED
												: SainAction.VIEW_TEAM_PAGE_CLICKED,
										});
									}}
									spacing="compact"
									appearance="subtle"
								>
									<Text color="color.text.subtlest" weight="medium">
										{isPerson
											? formatMessage(messages.ai_answer_who_questions_view_profile)
											: formatMessage(messages.knowledge_cards_view_team)}
									</Text>
								</LinkButton>
							) : (
								<Button
									onClick={() => {
										setExpanded((e) => !e);
										fireAnalyticsEvent(
											onKnowledgeCardLinkClick({ ...commonAttributes, location: 'show-more' }),
										);
										fireSAINUsageEvent({ metCriteria: SainAction.EXPAND_CLICKED });
									}}
									spacing="compact"
									appearance="subtle"
								>
									<Text color="color.text.subtlest" weight="medium">
										{formatMessage(messages.ai_answer_expand_button_label)}
									</Text>
								</Button>
							)}
						</Inline>
					) : null}
				</Box>
			</Stack>
		</Box>
	);
});
